import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Activity, Loader2 } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1512102438733-bfa4ed29aef7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxob3NwaXRhbCUyMG1vZGVybiUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzg2MzMyNzI2fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(username, password);
      navigate("/dashboard");
    } catch (err) {
      setError(apiError(err, "Login gagal"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      <div className="hidden lg:flex relative flex-col justify-between p-12 bg-primary overflow-hidden">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="relative flex items-center gap-3">
          <div className="w-11 h-11 rounded-md bg-white/15 flex items-center justify-center">
            <Activity className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="font-heading font-bold text-2xl text-white tracking-tight">SIPAT</p>
            <p className="text-emerald-100/80 text-xs">Sistem Informasi Pemeliharaan Aset Teknologi Informasi</p>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-heading text-4xl font-semibold text-white leading-tight tracking-tight">
            Kelola aset IT rumah sakit secara presisi dan terpantau.
          </h1>
          <p className="mt-4 text-emerald-100/80 text-base max-w-md">
            Pelacakan aset, jadwal maintenance, rekomendasi, dan laporan dalam satu sistem terpadu.
          </p>
        </div>
        <p className="relative text-emerald-100/60 text-xs">© 2026 SIPAT — Unit Teknologi Informasi</p>
      </div>
      <div className="flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <div className="w-10 h-10 rounded-md bg-primary flex items-center justify-center">
              <Activity className="w-5 h-5 text-primary-foreground" />
            </div>
            <p className="font-heading font-bold text-2xl text-primary">SIPAT</p>
          </div>
          <h2 className="font-heading text-3xl font-semibold tracking-tight">Masuk</h2>
          <p className="text-muted-foreground text-sm mt-1 mb-8">Gunakan akun IT atau Pimpinan Anda.</p>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                data-testid="login-username-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Masukkan username"
                autoComplete="username"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Masukkan password"
                autoComplete="current-password"
                required
              />
            </div>
            {error && (
              <p className="text-sm text-destructive" data-testid="login-error">{error}</p>
            )}
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Masuk ke SIPAT
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
